import React, { useState, useRef, useEffect } from "react";
import { features } from "app/features";
import { _t } from "app/i18n/i18n";
import { preventDefault } from "lib/utils";
import { ListGroupItem } from "react-bootstrap";
import { Suggestion } from "../AutoCompleteSearchBox/AutoCompleteSearchBox";
import { isMobile } from "lib/hooks/useIsMobile";
import { isIntranetUser } from "utils/htmlReportsUtils";
import { useSelector } from "react-redux";
import { DOMICILE } from "redux/slices/api/initialisePreferences";
import { isIntranetPath } from "../../lib/routing";

const RADIO_ALL = "all fields";
const RADIO_TITLE = "report title";
const RADIO_NAIRA = "naira";

export const KeywordSuggestions = ({
                                     keyword,
                                     setKeyword,
                                     onSubmit,
                                     forHtmlReport,
                                     onNairaSearch,
                                     nairaMode = false,
                                     setNairaMode = () => {
                                     }
                                   }) => {
  const nairaModeRef = useRef(nairaMode);
  nairaModeRef.current = nairaMode;

  const domicile = useSelector((state) => state.preferences[DOMICILE])[0];
  const [searchRadio, setSearchRadio] = useState(RADIO_ALL);
  useEffect(() => {
    if (nairaMode && searchRadio !== RADIO_NAIRA) {
      setSearchRadio(RADIO_NAIRA);
    } else if (!nairaMode && searchRadio === RADIO_NAIRA) {
      setSearchRadio(RADIO_ALL);
    }
  }, [nairaMode]);

  if (!features.autocompleteKeywordSuggestions || !keyword || !keyword.trim() || keyword.includes('"')) return null;

  const categoryName = searchRadio === RADIO_TITLE ? "title" : "";
  const reportTitlesLabel = searchRadio === RADIO_TITLE ? _t(" in report titles only") : "";

  const hasWordsLabel = (
    <>
      {_t("Has words: ")} <EM>{keyword}</EM> {reportTitlesLabel}
    </>
  );
  const exactPhraseLabel = (
    <>
      {_t("Exact phrase: ")} <EM>&quot;{keyword}&quot;</EM> {reportTitlesLabel}
    </>
  );
  const excludeWords = (
    <>
      {_t("Exclude words: ")} <EM>NOT &quot;{keyword}&quot;</EM> {reportTitlesLabel}
    </>
  );

  const onNairaAwareSubmit = (catName, value) => {
    if (nairaModeRef.current) {
      setKeyword("");
      if (onNairaSearch) {
        const valueForNaira = value.replace(/^"|"$/g, "");
        onNairaSearch(valueForNaira);
      }
    } else {
      if (onSubmit) onSubmit(catName, value);
    }
  };

  const onRadioChange = (val) => {
    const isNaira = val === RADIO_NAIRA && !forHtmlReport && isIntranetPath() && domicile !== "ja";
    nairaModeRef.current = isNaira;
    setSearchRadio(val);
    setNairaMode(isNaira);
  };

  return (
    <>
      <ListGroupItem className="border-bottom bg-white pl-0 pr-0">
        <h4 className="font-weight-light mb-0 pr-4">{_t("Search Options")}</h4>
        {AllFieldsTitleToggle(searchRadio, onRadioChange, forHtmlReport, domicile)}
      </ListGroupItem>
      <Suggestion
        suggestion={{CategoryName: categoryName, label: exactPhraseLabel, CategoryValue: `"${keyword}"`}}
        setKeyword={setKeyword}
        onSubmit={onNairaAwareSubmit}
        forHtmlReport={forHtmlReport}
        nairaMode={nairaMode}
        onNairaSearch={onNairaSearch}
      />
      {!nairaMode && (
        <>
          <Suggestion
            suggestion={{CategoryName: categoryName, label: hasWordsLabel, CategoryValue: keyword}}
            setKeyword={setKeyword}
            onSubmit={onNairaAwareSubmit}
            forHtmlReport={forHtmlReport}
          />
          <Suggestion
            suggestion={{CategoryName: categoryName, label: excludeWords, CategoryValue: 'NOT "' + keyword + '"'}}
            setKeyword={setKeyword}
            onSubmit={onNairaAwareSubmit}
            forHtmlReport={forHtmlReport}
          />
        </>
      )}
    </>
  );
};

const AllFieldsTitleToggle = (searchRadio, onRadioChange, forHtmlReport, domicile) => {
  const onChange = (ev) => onRadioChange(ev.target.id);
  const canShowNaira = !isMobile() && !forHtmlReport && isIntranetUser() && domicile !== "ja";
  return (
    <section onMouseDown={preventDefault} className="mt-1">
      <label id={RADIO_ALL} onClick={preventDefault} onMouseDown={onChange} className="mb-0">
        <input
          type="radio"
          id={RADIO_ALL}
          name="toggle"
          checked={searchRadio === RADIO_ALL}
          onChange={onChange}
          className="ml-2 mr-1 mb-0"
        />
        {_t("Search all fields")}
      </label>
      <label id={RADIO_TITLE} onClick={preventDefault} onMouseDown={onChange} className="mb-0">
        <input
          type="radio"
          id={RADIO_TITLE}
          name="toggle"
          checked={searchRadio === RADIO_TITLE}
          onChange={onChange}
          className="ml-2 mr-1 mb-0"
        />
        {_t("Report titles only")}
      </label>
      {canShowNaira && (
        <label id={RADIO_NAIRA} onClick={preventDefault} onMouseDown={onChange} className="mb-0">
          <input
            type="radio"
            id={RADIO_NAIRA}
            name="toggle"
            checked={searchRadio === RADIO_NAIRA}
            onChange={onChange}
            className="ml-2 mr-1 mb-0"
          />
          {_t("Search using NaiRA (AI Chat bot)")}
        </label>
      )}
    </section>
  );
};

const EM = ({ children }) => <em className="text-info">{children}</em>;
